/* ═════════════════════ the term and the week, held ═════════════════════

   `PLAN` and `WEEK` are what the engine last worked out, and `LEDGER` is its
   reasoning. They are **never** persisted: they are pure functions of `S`, the
   Google events, and the minute, so they are recomputed whenever a planning tab
   draws. There is no plan file, and therefore no stale plan to reconcile.

   That is why this module holds them rather than either tab. Both tabs read
   both, the task panel and the board and the series drawer all invalidate them,
   and none of those owns them.

   The memo is the whole of the performance story. `touchPlan()` is called from
   every mutation — from the mutation itself, not from `save()`, which is
   debounced 180ms and would make the horizon visibly lag a keystroke — and
   `recomputePlans()` does nothing unless the revision or the minute has moved.
   Same state, same minute, same answer.

   `sweepBlockRefs` is the one thing here that writes. A move and a miss are
   keyed by block id, and a block id is derived from state, so an item deleted
   in the task view can leave a ruling pointing at nothing. It drops only ids
   the engine did not just emit, which is why a live ruling never sweeps itself
   away. On the overwhelming majority of runs it changes nothing and does not
   save. */

import { S, save } from '../state/store.js';
import StudyHubEngine from '../core/engine.js';
import { startOfWeek, fromIsoDay, hhmmToMin } from '../core/time.js';

/** @type {StudyPlan} */
export let PLAN = { status: 'never', computedAt: null, message: '', horizon: null, weeks: [], insights: [], extracted: [] };

export function normalizePlan(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    status: r.status || 'never',
    computedAt: typeof r.computedAt === 'number' ? r.computedAt : null,
    message: r.message || '',
    horizon: r.horizon || null,
    weeks: (Array.isArray(r.weeks) ? r.weeks : []).filter((w) => w && w.start),
    insights: (Array.isArray(r.insights) ? r.insights : []).filter((i) => i && i.title),
    extracted: (Array.isArray(r.extracted) ? r.extracted : []).filter((x) => x && x.id && x.due),
  };
}

/** The plan's word on one week, keyed by the Monday it starts. */
export const planWeek = (mondayMs) => PLAN.weeks.find((w) => fromIsoDay(w.start) === mondayMs) || null;

/* ─────────────────── when the count is redone ───────────────────

   Neither plan is ever stored. Both are functions of the state, the Google
   sync and the minute, and a stored answer to a question that cheap is only a
   way of being confidently out of date — which is the exact failure
   weekPlanIsCurrent() was written to catch.

   So the plan is recomputed on the way into a render, and a revision counter
   decides whether that costs anything. Every mutation the planners can see
   bumps the counter; a render whose counter and minute are unchanged since the
   last run reuses what is already there, which is what lets a keystroke in the
   entry title redraw the horizon without the engine running twenty times.

   The counter is bumped from the mutation and not from save(): save() is
   debounced by 180ms, and a horizon that lags a keystroke by a fifth of a
   second is a horizon that looks broken. */

export let PLAN_REV = 0;
/** The engine's reasoning behind the current plan. @type {any} */
export let LEDGER = null;
export let planMemo = { rev: -1, minute: -1 };

/** Something the planners care about has changed. */
export function touchPlan() { PLAN_REV++; }

/** Redraw whichever planning tab happens to be on screen.

    The task editors, the deadline list and the series drawer all change what
    the term is made of from other tabs entirely, and before the engine they
    had no way of saying so: only showTab() ever refreshed the horizon, so a
    deadline added on Monday reached the horizon on the next tab switch. */

export function recomputePlans(force = false) {
  const minute = Math.floor(Date.now() / 60000);
  if (!force && planMemo.rev === PLAN_REV && planMemo.minute === minute) return;

  const r = StudyHubEngine.recompute();
  PLAN = normalizePlan(r.plan);
  WEEK = normalizeWeek(r.week);
  LEDGER = r.ledger;
  planMemo = { rev: PLAN_REV, minute };
  sweepBlockRefs();
}

/* What the user has ruled on is keyed by block id, and a block id no longer
   emitted refers to nothing — an item deleted, an estimate edited down until a
   session disappeared. Those entries are dropped so neither map grows forever.

   It is deliberately conservative: a missed block and a moved one are both
   re-emitted with their original ids precisely so that a live ruling can never
   sweep itself away. On the overwhelming majority of runs nothing is touched
   and nothing is written.

   And it does not run at all on a week the planner failed to produce. A broken
   run emits no blocks, so every id would look dangling and one bad afternoon
   would silently erase every "couldn't make it" and every move the user has
   ever recorded. Those are the only things in here she typed herself; a
   garbage collector that cannot tell an empty answer from no answer has no
   business near them. */
export function sweepBlockRefs() {
  if (WEEK.status !== 'ok') return;

  const live = new Set(WEEK.blocks.map((b) => b.id));
  let touched = false;

  for (const id of Object.keys(S.weekly.moved)) {
    if (!live.has(id)) { delete S.weekly.moved[id]; touched = true; }
  }
  const keep = S.weekly.misses.filter((m) => live.has(m.blockId));
  if (keep.length !== S.weekly.misses.length) { S.weekly.misses = keep; touched = true; }

  if (touched) save();
}

/* ─────────────────── what sits on the horizon ─────────────────── */

/* studyHub's own deadlines and events belong on the horizon too — they are the
   same term. They are shown, and priced, alongside everything typed in here,
   but they are not editable here: the tab that owns a thing is the tab that
   edits it. */

export const wkStart = () => startOfWeek(new Date()).getTime();

/** @type {StudyWeek} */
export let WEEK = { status: 'never', plannedAt: null, message: '', weekStart: null, blocks: [], notes: [], days: [] };

/* Every field the engine emits has to be named here or it is dropped on the way
   in — and dropped silently, because the node tests read `recompute()` directly
   and never travel this path. A new field on the week output means a new line in
   this list, in the same commit. */
export function normalizeWeek(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    status: r.status || 'never',
    plannedAt: typeof r.plannedAt === 'number' ? r.plannedAt : null,
    message: r.message || '',
    weekStart: r.weekStart || null,
    blocks: (Array.isArray(r.blocks) ? r.blocks : [])
      .filter((b) => b && b.id && b.day && hhmmToMin(b.start) != null && hhmmToMin(b.end) != null),
    notes: (Array.isArray(r.notes) ? r.notes : []).filter((n) => n && n.title),
    days: (Array.isArray(r.days) ? r.days : [])
      .filter((d) => d && typeof d.iso === 'string' && typeof d.dayIdx === 'number'),
  };
}

/* A plan is only about one week. Showing last week's blocks over this week's
   days would be worse than showing nothing, so a stale plan is ignored for
   drawing while its status line still says when it was made. */
export const weekPlanIsCurrent = () => !!WEEK.weekStart && fromIsoDay(WEEK.weekStart) === wkStart();
